import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Badge from '../components/Badge';
import ConfirmDialog from '../components/ConfirmDialog';
import { stores } from '../../services/apiClient';

const statusOptions = ['all', 'open', 'closed', 'active', 'pending', 'rejected', 'suspended'];
const voucherStatusOptions = ['all', 'pending', 'approved', 'rejected'];

function formatVoucherStatus(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'approved') return 'Aprovado';
  if (normalized === 'rejected') return 'Rejeitado';
  if (normalized === 'pending') return 'Pendente';
  return '-';
}

function formatTaxProfileStatus(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'approved') return 'Aprovado';
  if (normalized === 'rejected') return 'Rejeitado';
  if (normalized === 'pending') return 'Pendente';
  return 'Nao cadastrado';
}

export default function AdminStores() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [status, setStatus] = useState('all');
  const [voucherStatus, setVoucherStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    const statusParam = (searchParams.get('status') || '').toLowerCase();
    const voucherStatusParam = (searchParams.get('voucher_status') || '').toLowerCase();
    const queryParam = searchParams.get('q') || '';

    setStatus(statusParam && statusOptions.includes(statusParam) ? statusParam : 'all');
    setVoucherStatus(voucherStatusParam && voucherStatusOptions.includes(voucherStatusParam) ? voucherStatusParam : 'all');
    setQuery(queryParam);
  }, [searchParams]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const result = await stores.list({
          status: status === 'all' ? undefined : status,
          q: query || undefined,
          voucherStatus: voucherStatus === 'all' ? undefined : voucherStatus,
        });
        if (mounted) setData(result);
      } catch {
        if (mounted) setError('Erro ao carregar lojas');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [status, voucherStatus, query]);

  const columns = useMemo(() => [
    { key: 'name', header: 'Loja', render: (row) => (<Link style={{ color: '#FF6A00' }} to={`/admin/lojas/${row.id}`}>{row.name}</Link>) },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const variant = row.status === 'active' ? 'success' : row.status === 'pending' ? 'warning' : row.status === 'rejected' ? 'danger' : 'neutral';
        return <Badge variant={variant}>{row.status}</Badge>;
      }
    },
    {
      key: 'voucher_approval_status',
      header: 'Voucher',
      render: (row) => formatVoucherStatus(row.voucher_approval_status),
    },
    {
      key: 'voucher_enabled_for_checkout',
      header: 'Apto voucher',
      render: (row) => (row.voucher_enabled_for_checkout ? 'Sim' : 'Nao'),
    },
    {
      key: 'tax_profile_status',
      header: 'Fiscal',
      render: (row) => formatTaxProfileStatus(row.tax_profile_status),
    },
    {
      key: 'tax_profile_complete',
      header: 'Fiscal completo',
      render: (row) => (row.tax_profile_complete ? 'Sim' : 'Nao'),
    },
    { key: 'gmv_day', header: 'GMV dia', render: (row) => `R$ ${Number(row.gmv_day || 0).toFixed(2)}` },
    { key: 'gmv_month', header: 'GMV mes', render: (row) => `R$ ${Number(row.gmv_month || 0).toFixed(2)}` },
    { key: 'gmv_year', header: 'GMV ano', render: (row) => `R$ ${Number(row.gmv_year || 0).toFixed(2)}` },
    { key: 'orders_day', header: 'Pedidos dia' },
    { key: 'avg_ticket', header: 'Ticket medio', render: (row) => `R$ ${Number(row.avg_ticket || 0).toFixed(2)}` },
    { key: 'fee_percent', header: 'Taxa %', render: (row) => `${row.fee_percent}%` },
    {
      key: 'actions',
      header: 'Acoes',
      render: (row) => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="admin-button secondary" onClick={() => setConfirm({ type: 'approve', id: row.id })}>Aprovar</button>
          <button className="admin-button secondary" onClick={() => setConfirm({ type: 'reject', id: row.id })}>Reprovar</button>
          <button
            className="admin-button secondary"
            onClick={() => navigate(`/admin/lojas/${row.id}?tab=Pedidos&status=completed`)}
          >
            Resulmo
          </button>
        </div>
      )
    }
  ], [navigate]);

  const handleConfirm = async () => {
    if (!confirm) return;
    const reason = prompt('Motivo (opcional)');
    if (confirm.type === 'approve') {
      await stores.approve(confirm.id, reason || undefined);
    } else {
      if (!reason) return alert('Motivo obrigatorio');
      await stores.reject(confirm.id, reason);
    }
    setConfirm(null);
    const result = await stores.list({
      status: status === 'all' ? undefined : status,
      q: query || undefined,
      voucherStatus: voucherStatus === 'all' ? undefined : voucherStatus,
    });
    setData(result);
  };

  return (
    <div className="admin-section">
      <PageHeader title="Lojas" subtitle="Gerencie lojas, status e metricas" />

      <div className="admin-filters">
        <select className="admin-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          {statusOptions.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <select className="admin-select" value={voucherStatus} onChange={(e) => setVoucherStatus(e.target.value)}>
          {voucherStatusOptions.map((option) => (
            <option key={option} value={option}>
              {option === 'all' ? 'Voucher: todos' : `Voucher: ${option}`}
            </option>
          ))}
        </select>
        <input className="admin-input" placeholder="Buscar loja" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      <DataTable columns={columns} data={data} loading={loading} error={error} emptyMessage="Nenhuma loja encontrada" />

      <ConfirmDialog
        open={!!confirm}
        title="Confirmar acao"
        description="Deseja confirmar esta acao?"
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
